import type { ReactNode } from 'react'

interface CardProps {
  children: ReactNode
  className?: string
  hover?: boolean
}

export default function Card({ children, className = '', hover = false }: CardProps) {
  return (
    <div
      className={`
        bg-white border border-slate-200 rounded-xl p-6 shadow-card
        ${hover ? 'transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-card-hover' : ''}
        ${className}
      `}
    >
      {children}
    </div>
  )
}

interface FeatureCardProps {
  icon: ReactNode
  title: string
  description: string
  className?: string
}

export function FeatureCard({ icon, title, description, className = '' }: FeatureCardProps) {
  return (
    <Card hover className={className}>
      <div className="w-12 h-12 rounded-lg bg-teal-50 flex items-center justify-center text-accent-teal mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-serif font-medium text-brand-light mb-2">{title}</h3>
      <p className="text-brand-muted text-[15px] leading-relaxed">{description}</p>
    </Card>
  )
}

interface TestimonialCardProps {
  quote: string
  author: string
  role: string
  company?: string
  rating?: number
  className?: string
}

export function TestimonialCard({
  quote,
  author,
  role,
  company,
  rating = 5,
  className = '',
}: TestimonialCardProps) {
  return (
    <Card className={className}>
      {/* Star Rating */}
      {rating > 0 && (
        <div className="flex gap-1 mb-4">
          {Array.from({ length: rating }).map((_, i) => (
            <svg
              key={i}
              className="w-5 h-5 text-star"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
      )}

      {/* Quote */}
      <blockquote className="text-brand-light text-[15px] leading-relaxed mb-4">
        "{quote}"
      </blockquote>

      {/* Author */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-accent-teal text-sm font-medium">
          {author
            .split(' ')
            .map((n) => n[0])
            .join('')}
        </div>
        <div>
          <p className="text-brand-light text-sm font-medium">{author}</p>
          <p className="text-brand-muted text-xs">
            {role}
            {company && `, ${company}`}
          </p>
        </div>
      </div>
    </Card>
  )
}
